/**
 * The SFOC chart's appearance: its own `PlotState`, edited by the same chart
 * gear Explore has (`<plot-settings-menu .target=${sfocChartStyle}>`).
 *
 * Its own rather than Explore's `plotState`, because Explore's describes the
 * Plot tab's chart - its type, axes and columns as well as its looks - and the
 * SFOC chart is always a line of model SFOC over engine load. It starts from
 * the person's remembered chart preferences, so text size and line width match
 * what they chose for Explore; changes made here last for the session and are
 * not written back as preferences.
 */

import { Atom } from '../../state/atom.ts';
import { seededPlotState, type PlotState, type PlotStyle } from '../../state/plot-state.ts';
import type { ChartStyleTarget } from '../plot/plot-settings-menu.ts';
import { sfocMeasured } from './sfoc-measured.ts';
import { sfocLayout } from '../../state/store.ts';

function initialState(): PlotState {
  const seeded = seededPlotState();
  return { ...seeded, plot_type: 'line' };
}

export const sfocPlotState = new Atom<PlotState>(initialState());

export const sfocChartStyle: ChartStyleTarget = {
  kind: 'embedded',
  state: () => sfocPlotState.value,
  setStyle<K extends keyof PlotStyle>(key: K, value: PlotStyle[K]): void {
    sfocPlotState.update((s) => ({ ...s, style: { ...s.style, [key]: value } }));
  },
  setColorScale(name: string): void {
    sfocPlotState.update((s) => ({ ...s, color_scale: name }));
  },
  // Curves are coloured by fuel, shaded per engine and ship - the ship colour
  // modes have nothing left to allocate, so the gear does not offer them.
  multiShip: () => false,
  // The curves are lines; the measured points behind them are a scatter or a
  // heatmap, and the gear offers Explore's controls for whichever is showing.
  styleKinds() {
    const mode = sfocMeasured.value;
    if (mode === 'scatter') return ['line', 'scatter'];
    if (mode === 'heatmap') return ['line', 'heatmap'];
    return ['line'];
  },
  gridActive: () => sfocLayout.value === 'grid',
  subscribe(listener: () => void): () => void {
    const offs = [
      sfocPlotState.subscribe(() => listener()),
      sfocMeasured.subscribe(() => listener()),
      sfocLayout.subscribe(() => listener()),
    ];
    return () => offs.forEach((off) => off());
  },
};
